/**
 * Companies — show view, on the shared Inspector grammar
 * (design-system §5 / coverage §2.1). Header / Overview / Relations / Audit.
 *
 * Relations: the company's child Projects (a filtered back-reference read,
 * degrading to a calm "None." when empty). Audit renders the generic
 * `/api/companies/:id/timeline` teaser until that endpoint ships (`[P]`).
 *
 * Composes the lower-level Inspector pieces directly (rather than `ShowPage`)
 * so the back-reference `useList` hook runs unconditionally at the top of a
 * real component — never inside a render-prop.
 */
import {
  useShow,
  useGo,
  useNavigation,
  useList,
  type HttpError,
} from "@refinedev/core";
import { Pencil, FolderKanban } from "@/components/ui/icon";
import type { Company, Project } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  FieldGrid,
  Field,
  RefLink,
  RelationsBlock,
  AuditBlock,
} from "@/components/inspector";

export function CompanyShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Company, HttpError>({ resource: "companies" });
  const { data, isLoading, isError, error, refetch } = query;
  const company = data?.data;

  // Back-reference: child Projects. Hook runs unconditionally; `enabled` gates
  // the actual fetch until the company id is known.
  const { data: projectsData } = useList<Project, HttpError>({
    resource: "projects",
    filters: company
      ? [{ field: "company_id", operator: "eq", value: company.id }]
      : [],
    pagination: { pageSize: 25 },
    queryOptions: { enabled: Boolean(company), retry: false },
    successNotification: false,
    errorNotification: false,
  });
  const projects = projectsData?.data ?? [];

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !company) {
    return (
      <ShowPageError
        message={error?.message}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Inspector>
      <InspectorHeader
        title={company.name}
        subtitle="An organisation that owns projects and work."
        status={company.status}
        primaryAction={
          <Button
            variant="outline"
            onClick={() => go({ to: editUrl("companies", company.id) })}
          >
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        }
        menuActions={[
          {
            label: "New project here",
            onClick: () =>
              go({
                to: { resource: "projects", action: "create" },
                query: { company_id: company.id },
              }),
          },
        ]}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Created">
            {new Date(company.created_at).toLocaleString()}
          </Field>
          <Field label="Updated">
            {new Date(company.updated_at).toLocaleString()}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Projects",
              emptyMessage: "No projects in this company yet.",
              items: projects.map((p) => ({
                key: `project:${p.id}`,
                label: "Project",
                icon: FolderKanban,
                node: <RefLink resource="projects" id={p.id} label={p.name} />,
              })),
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={company.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="companies" entityId={company.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default CompanyShow;
